import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFab,
  IonFooter,
  IonHeader,
  IonIcon,
  IonLabel,
  IonPopover,
  IonRefresher,
  IonRefresherContent,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonLoading,
  useIonToast
} from "@ionic/react";
import {Trans, useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {
  AddTicketMessageDocument,
  ChangeTicketStatusDocument,
  DeleteTicketDocument,
  GetTicketDocument,
  type GetTicketQuery,
  type TicketMessageFragment,
  TicketStatus,
  type UserFragment
} from "app/gql/graphqlSchema";
import {useQuery} from "app/employee/controlCard/hook/useQuery";
import {useCallback, useRef, useState} from "react";
import {useEmployeeClientId} from "app/employee/state/bootstrapEmployee/selectors";
import {LoadingLoadable} from "app/employee/Loading";
import {FlexColumn} from "app/employee/flexUtils";
import {
  Avatar,
  ChatBubbleLeft,
  ChatBubbleRight,
  getInitials,
  MessageRowLeft,
  MessageRowRight,
  MessagesContainer,
  MessageTime,
  SystemMessage
} from "app/employee/ticket/ChatBubble";
import {linkEmplTickets} from "app/employee/core/employeeLinks";
import {useDismiss} from "app/employee/incdoc/useDismiss";
import {Deferred} from "app/utils/deferred";
import {getUploadyDestination, query} from "app/gql/client";
import {cameraOutline, ellipsisVerticalCircleOutline, refresh, sendOutline} from "ionicons/icons";
import {styled} from "styled-components";
import {useBootstrapEmployeeStore} from "app/employee/state/bootstrapEmployee/mutations";
import {selectMaybeLoaded} from "app/state/common/loadable";
import {Uploady} from "@rpldy/uploady";
import {useAttachmentCollectionEditor} from "app/components/hooks/useAttachmentCollectionEditor";
import {CompactAttachmentStrip} from "app/employee/ticket/CompactAttachmentStrip";
import {TicketNumber} from "app/employee/ticket/TicketNumber";
import {TicketSummary} from "app/employee/ticket/TicketSummary";
import {AddEditTicket} from "app/employee/ticket/AddEditTicket";
import {FstIonModal} from "app/employee/incdoc/FstIonModal";
import {AttachmentCollection} from "app/employee/att/AttachmentCollection";
import {Timestamp} from "app/employee/Timestamp";
import {MenuButton} from "app/employee/menu/MenuButton";
import {useTicketActions} from "app/employee/ticket/useTicketActions";
import {UnreachableCaseError} from "ts-essentials";
import {normalizeWhitespace} from "app/utils/stdlib";
import {type PrintTicket, PrintType, savePrintState} from "app/state/print";
import {linkPdfExport} from "app/components/hooks/useLinks";
import {useShareAction} from "app/employee/hooks/useShareAction";
import {formatTicketNumber} from "app/components/admin/ticket/TicketNumber";
import {usePollVisible} from "app/components/hooks/usePollVisible";
import {useIonViewEffect} from "app/employee/hooks/useIonViewEffect";

const TICKET_POLL_INTERVAL_MS = 60 * 1000;

const stateMapper = (result: GetTicketQuery) => result.getTicket;

const StyledTextarea = styled(IonTextarea)`
  --padding-top: 0.25rem !important;
  --padding-bottom: 0.25rem !important;
  --padding-start: 0.5rem !important;
  --padding-end: 0.5rem !important;
`;

const FooterActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 0.25rem;
  align-items: center;
  padding: 0.25rem;
`;

const CompactButton = styled(IonButton)`
  display: block;
  min-width: 3rem;
  height: 3rem;
  font-size: 1rem;
  --padding-top: 0rem !important;
  --padding-bottom: 0rem !important;
  --padding-start: 0rem !important;
  --padding-end: 0rem !important;
`;

const UploadButtonWrapper = styled.div`
  margin-top: 0.5rem;
`;

const BottomPadding = styled.div`
  height: 5rem;
`;

const getUserName = (user: UserFragment) => user.name;

const isCurrentEmployee = (user: UserFragment, currentEmployeeId?: string) => {
  if (!currentEmployeeId) return false;
  return user.__typename === "Employee" && user.employeeId.employeeId === currentEmployeeId;
};

const useCurrentEmployeeId = () => {
  const bootstrapResult = useBootstrapEmployeeStore((state) => selectMaybeLoaded(state.bootstrapResult));
  if (bootstrapResult?.__typename === "BootstrapEmployee") {
    return bootstrapResult.employee.id.employeeId;
  }
  return undefined;
};

const MessageAttachments = ({message}: {message: TicketMessageFragment}) => {
  if (message.__typename !== "UserTicketMessage") return null;
  if (message.attachmentCollection.attachments.length === 0) return null;

  return (
    <AttachmentCollection
      attCollection={message.attachmentCollection}
      canDelete={false}
      canUpload={false}
      hideHeader
      name={message.content}
    />
  );
};

const MessageBubble = ({message, currentEmployeeId}: {message: TicketMessageFragment; currentEmployeeId?: string}) => {
  const {t} = useTranslation();
  if (message.__typename === "TicketStatusMessage") {
    return (
      <SystemMessage>
        <Trans
          components={{
            Name: <strong>{getUserName(message.author)}</strong>,
            Status: <strong>{t(`ticket.status.${message.newStatus}`)}</strong>
          }}
          i18nKey="ticket.detail.status-changed"
          t={t}
        />
        <MessageTime>
          <Timestamp ts={message.createdAt} />
        </MessageTime>
      </SystemMessage>
    );
  } else if (message.__typename === "UserTicketMessage") {
    const isMine = isCurrentEmployee(message.author, currentEmployeeId);
    const name = getUserName(message.author);

    if (isMine) {
      return (
        <MessageRowRight>
          <ChatBubbleRight>
            {message.content}
            <MessageAttachments message={message} />
          </ChatBubbleRight>
        </MessageRowRight>
      );
    }

    const avatarTriggerId = `avatar-${message.id.messageId}`;

    return (
      <MessageRowLeft>
        <Avatar id={avatarTriggerId}>{getInitials(name)}</Avatar>
        <IonPopover arrow trigger={avatarTriggerId} triggerAction="click">
          <IonContent className="ion-padding">
            <strong>{name}</strong>
            <div>
              <Timestamp ts={message.createdAt} />
            </div>
          </IonContent>
        </IonPopover>
        <ChatBubbleLeft>
          {message.content}
          <MessageAttachments message={message} />
        </ChatBubbleLeft>
      </MessageRowLeft>
    );
  } else {
    throw new UnreachableCaseError(message);
  }
};

const TicketDetailInner = () => {
  const {t} = useTranslation();
  const {ticketId} = useParams<{ticketId: string}>();
  const clientId = useEmployeeClientId();
  const currentEmployeeId = useCurrentEmployeeId();
  const [messageText, setMessageText] = useState("");
  const [sending, setSending] = useState(false);

  const contentRef = useRef<HTMLIonContentElement>(null);
  const editModalRef = useRef<HTMLIonModalElement>(null);
  const {data, refetch, fetch} = useQuery(GetTicketDocument, stateMapper);
  const {showFileUpload, unifiedAttachments, uploadProgress, cleanup, collectOcrFiles} =
    useAttachmentCollectionEditor(null);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      contentRef.current?.scrollToBottom(300);
    });
  }, []);

  const doFetch = useCallback(() => {
    fetch({id: {clientId, ticketId}});
  }, [clientId, fetch, ticketId]);

  const {isPageVisible} = usePollVisible(
    refetch,
    TICKET_POLL_INTERVAL_MS,
    "Setting up periodic refresh of ticket detail (employee)",
    "Stop periodic refresh of ticket detail (employee)",
    "Refreshing ticket detail (employee)"
  );

  useIonViewEffect(() => {
    if (isPageVisible) {
      doFetch();
    }
  }, [doFetch, isPageVisible]);

  const handleSendMessage = async () => {
    const content = normalizeWhitespace(messageText);
    const {stagedUploadIds} = collectOcrFiles();
    if (!content && stagedUploadIds.length === 0) return;
    if (!data.value) return;

    setSending(true);
    try {
      await query(AddTicketMessageDocument, {
        input: {
          ticketId: {clientId, ticketId},
          content,
          stageUploadIds: stagedUploadIds
        }
      });
      setMessageText("");
      cleanup();
      await refetch();
      scrollToBottom();
    } finally {
      setSending(false);
    }
  };

  const ticket = data.value;
  const hasAttachments = unifiedAttachments.length > 0;

  const uploadButton = (
    <CompactButton
      disabled={sending}
      fill="outline"
      onClick={() => showFileUpload()}
      shape="round"
      size="small"
      slot="start"
    >
      <IonIcon icon={cameraOutline} slot="icon-only" />
    </CompactButton>
  );

  const handleChangeStatus = useCallback(
    async (status: TicketStatus) => {
      await query(ChangeTicketStatusDocument, {
        id: {clientId, ticketId},
        status
      });
      await refetch();
    },
    [clientId, ticketId, refetch]
  );

  const [presentAlert] = useIonAlert();
  const dismiss = useDismiss(linkEmplTickets());
  const isSender = ticket ? isCurrentEmployee(ticket.sender, currentEmployeeId) : false;
  const isParticipant =
    isSender || (ticket?.recipients.some((r) => isCurrentEmployee(r.recipient, currentEmployeeId)) ?? false);

  const handleDelete = useCallback(async () => {
    const deferred = new Deferred<boolean>();
    await presentAlert({
      header: t("ticket.delete.confirm-title"),
      message: t("ticket.delete.confirm-message"),
      buttons: [
        {text: t("g.cancel"), handler: () => deferred.resolve(false), role: "cancel"},
        {text: t("g.delete"), role: "destructive", handler: () => deferred.resolve(true)}
      ]
    });
    if (await deferred.promise) {
      await query(DeleteTicketDocument, {id: {clientId, ticketId}});
      await dismiss();
    }
  }, [clientId, ticketId, dismiss, presentAlert, t]);

  const shareAttach = useShareAction();
  const [presentLoading, dismissLoading] = useIonLoading();

  const withPdfLink = useCallback(
    async (action: (url: string) => void | Promise<void>) => {
      if (!ticket) return;
      const printState: PrintTicket = {type: PrintType.TICKET, ticket, messages: ticket.messages};
      const humanName = `Сигнал ${formatTicketNumber(ticket.id)} — ${ticket.subject}`;
      let url: string;
      try {
        await presentLoading({message: t("g.share.download.downloading")});
        url = linkPdfExport(await savePrintState(printState, humanName));
      } finally {
        await dismissLoading();
      }
      await action(url);
    },
    [dismissLoading, presentLoading, t, ticket]
  );

  const handlePrint = useCallback(
    () =>
      withPdfLink((url) => {
        window.open(url, "_blank", "noopener");
      }),
    [withPdfLink]
  );

  const handleShare = useCallback(
    () =>
      withPdfLink((url) => {
        if (!ticket) return;
        const humanName = `Сигнал ${formatTicketNumber(ticket.id)} — ${ticket.subject}`;
        return shareAttach({url, humanName, filename: `${humanName}.pdf`});
      }),
    [shareAttach, ticket, withPdfLink]
  );

  const {showActions, showStatusActions} = useTicketActions({
    currentStatus: ticket?.status ?? TicketStatus.Todo,
    onEdit: isSender ? () => editModalRef.current?.present() : undefined,
    onDelete: isSender ? handleDelete : undefined,
    onChangeStatus: isParticipant ? handleChangeStatus : undefined,
    onPrint: ticket ? handlePrint : undefined,
    onShare: ticket ? handleShare : undefined
  });
  const [presentToast] = useIonToast();

  const handleRefresh = useCallback(async () => {
    await refetch();
    scrollToBottom();
    await presentToast({message: t("ticket.detail.refreshed"), duration: 1000, position: "top", color: "success"});
  }, [presentToast, refetch, scrollToBottom, t]);

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={linkEmplTickets()} />
          </IonButtons>
          <IonTitle>
            {t("ticket.detail.title")} {ticket ? <TicketNumber ticketId={ticket.id} /> : null}
          </IonTitle>
          <MenuButton>
            <IonButton color="primary" onClick={handleRefresh} slot="end">
              <IonIcon icon={refresh} slot="icon-only" />
            </IonButton>
          </MenuButton>
        </IonToolbar>
      </IonHeader>
      <IonContent ref={contentRef}>
        <LoadingLoadable loadable={data} />
        <IonRefresher onIonRefresh={refetch} slot="fixed">
          <IonRefresherContent />
        </IonRefresher>
        {ticket && (
          <FlexColumn>
            <TicketSummary
              canEdit={isSender}
              onAttachmentChanged={refetch}
              onChangeStatus={isParticipant ? showStatusActions : undefined}
              ticket={ticket}
            />
            <MessagesContainer>
              {ticket.messages.map((msg) => (
                <MessageBubble currentEmployeeId={currentEmployeeId} key={msg.id.messageId} message={msg} />
              ))}
            </MessagesContainer>
          </FlexColumn>
        )}
        <BottomPadding />
        <IonFab horizontal="end" slot="fixed" vertical="bottom">
          <IonButton onClick={showActions} shape="round">
            <IonLabel>{t("g.action")}</IonLabel>
            <IonIcon icon={ellipsisVerticalCircleOutline} slot="end" />
          </IonButton>
        </IonFab>
        {ticket && (
          <FstIonModal ref={editModalRef}>
            <AddEditTicket onCompleted={refetch} ticket={ticket} />
          </FstIonModal>
        )}
      </IonContent>
      {isParticipant && (
        <IonFooter>
          <FooterActions>
            {!hasAttachments && uploadButton}

            <StyledTextarea
              autocapitalize="on"
              fill="outline"
              onIonInput={(e) => setMessageText(e.detail.value ?? "")}
              placeholder={t("ticket.detail.message-placeholder")}
              value={messageText}
            />
            <CompactButton
              disabled={(!messageText.trim() && !hasAttachments) || sending || !uploadProgress.completed}
              expand="block"
              onClick={handleSendMessage}
              slot="end"
            >
              <IonIcon icon={sendOutline} slot="icon-only" />
            </CompactButton>
          </FooterActions>
          {hasAttachments && (
            <FooterActions>
              <UploadButtonWrapper>{uploadButton}</UploadButtonWrapper>
              <CompactAttachmentStrip unifiedAttachments={unifiedAttachments} />
            </FooterActions>
          )}
        </IonFooter>
      )}
    </>
  );
};

export const TicketDetailPage = () => (
  <Uploady concurrent destination={getUploadyDestination()} maxConcurrent={2} method="POST" multiple>
    <TicketDetailInner />
  </Uploady>
);
